/**
 * Where the session lives between page loads.
 *
 * The API signs asymmetrically and returns a short-lived access token (15
 * minutes) alongside a long-lived refresh token. Neither is an httpOnly cookie:
 * the API returns them in the response body, so the browser cannot hold them
 * for us and this module is the whole of our persistence.
 *
 * localStorage, not sessionStorage. sessionStorage is per-tab, so opening a
 * ride in a second tab logged the user out of it, which the previous
 * implementation did, and which is why `window.location.href` assignments were
 * scattered through the stores to paper over it.
 *
 * Every access is wrapped: Safari's private mode, a browser configured to block
 * site data, and an embedded webview can all make `localStorage` itself throw
 * on read. A session that cannot be persisted is a session that lasts until the
 * tab closes, which is a degradation, not a crash.
 */

import type { TokenPair, User } from "../api/types";

const ACCESS = "hb.accessToken";
const REFRESH = "hb.refreshToken";
const EXPIRES = "hb.expiresAt";
const USER = "hb.user";

/** Keys written by the pre-RTK implementation, cleared on first load. */
const LEGACY_KEYS = [
    "accessToken",
    "user",
    "userStatus",
    "isLoggedIn",
    "profile",
    "createProfileDetails",
];

function read(key: string): string | null {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
}

function write(key: string, value: string): void {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // Storage is unavailable or full. The in-memory Redux state still has
        // the session, so this degrades to "logged out on reload".
    }
}

function remove(key: string): void {
    try {
        window.localStorage.removeItem(key);
    } catch {
        // Nothing to do: if we cannot remove it we also could not have written it.
    }
}

export interface StoredSession {
    accessToken: string;
    refreshToken: string;
    expiresAt: string;
    user: User | null;
}

export const tokenStorage = {
    load(): StoredSession | null {
        const accessToken = read(ACCESS);
        const refreshToken = read(REFRESH);
        if (!accessToken || !refreshToken) return null;

        let user: User | null = null;
        const raw = read(USER);
        if (raw) {
            try {
                user = JSON.parse(raw) as User;
            } catch {
                // A corrupt cached user is not a reason to discard a valid
                // session: /users/me refetches it on the next render.
                remove(USER);
            }
        }

        return {
            accessToken,
            refreshToken,
            expiresAt: read(EXPIRES) ?? "",
            user,
        };
    },

    saveTokens(tokens: TokenPair): void {
        write(ACCESS, tokens.accessToken);
        write(REFRESH, tokens.refreshToken);
        write(EXPIRES, tokens.expiresAt);
    },

    saveUser(user: User): void {
        write(USER, JSON.stringify(user));
    },

    clear(): void {
        [ACCESS, REFRESH, EXPIRES, USER].forEach(remove);
    },

    /**
     * Drops keys the MobX stores wrote. Called once at startup so a returning
     * user is not left with a stale Firebase id token under "accessToken" that
     * nothing reads any more but a future bug might.
     */
    clearLegacy(): void {
        LEGACY_KEYS.forEach((key) => {
            remove(key);
            try {
                window.sessionStorage.removeItem(key);
            } catch {
                // As above.
            }
        });
    },
};

export default tokenStorage;
